import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Autocomplete,
    AutocompleteItem,
    Textarea,
} from "@heroui/react";
import { useEffect } from "react";
import { usePostData, useFetchData } from "@/hooks";
import { useForm, Controller } from "react-hook-form";
import { showToast } from "@/utils";
import { ThemedText } from "@/components/ui";

const BarakFormModal = ({
    isOpen,
    onClose,
    mode = "create",
    initialData = null,
    coopId = null,
    onSuccess,
}) => {
    const isEdit = mode === "edit";

    const defaultValues = {
        name: "",
        pic_id: "",
        description: "",
        coop_id: coopId,
    };

    const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues,
    });

    // Fetch Staff for PIC
    const { data: staffList, isLoading: isStaffLoading } =
        useFetchData("select/staff");

    // Mutation
    const endpoint = isEdit
        ? `barracks/${initialData?.id}/update`
        : "barracks/store";
    const mutation = usePostData(endpoint);

    useEffect(() => {
        if (isOpen) {
            reset(initialData || defaultValues);
        }
    }, [isOpen, initialData, reset]);

    const handleClose = () => {
        reset(defaultValues);
        onClose();
    };

    const onSubmit = (data) => {
        const payload = { ...data, coop_id: coopId };

        mutation.mutate(payload, {
            method: isEdit ? "put" : "post",
            onSuccess: () => {
                showToast(
                    "success",
                    isEdit ? "Update Successful" : "All Set!",
                    isEdit
                        ? "Barak updated successfully!"
                        : "Barak created successfully!",
                );
                onSuccess?.();
                handleClose();
            },
            onError: (err) => {
                console.error("Failed to submit barrack:", err);
                let message =
                    "Terjadi kesalahan pada sistem. Mohon coba beberapa saat lagi.";

                if (err.response?.status === 422) {
                    message = "Mohon periksa kembali input Anda.";
                }

                showToast("error", "Gagal Menyimpan", message);
            },
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onOpenChange={(open) => !open && handleClose()}
            placement="center"
            classNames={{
                header: "border-b border-default-100",
                footer: "border-t border-default-100",
            }}
            isDismissable={!mutation.isLoading}
            isKeyboardDismissDisabled={mutation.isLoading}
            hideCloseButton={mutation.isLoading}
        >
            <ModalContent>
                {() => (
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <ModalHeader className="flex flex-col gap-1">
                            <ThemedText size="md">
                                {isEdit ? "Edit Barak" : "Tambah Barak Baru"}
                            </ThemedText>
                        </ModalHeader>
                        <ModalBody className="py-4">
                            <Input
                                autoFocus
                                label="Nama Barak"
                                placeholder="Masukkan nama barak"
                                variant="bordered"
                                isRequired
                                isInvalid={!!errors.name}
                                errorMessage={errors.name?.message}
                                {...register("name", {
                                    required: "Nama barak wajib diisi",
                                })}
                            />

                            <Controller
                                name="pic_id"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        label="PIC Barak"
                                        placeholder="Pilih PIC"
                                        variant="bordered"
                                        defaultItems={staffList || []}
                                        isLoading={isStaffLoading}
                                        selectedKey={
                                            field.value
                                                ? String(field.value)
                                                : null
                                        }
                                        onSelectionChange={field.onChange}
                                    >
                                        {(item) => (
                                            <AutocompleteItem key={item.value}>
                                                {item.title}
                                            </AutocompleteItem>
                                        )}
                                    </Autocomplete>
                                )}
                            />

                            <Textarea
                                label="Catatan Tambahan"
                                placeholder="Isikan catatan tambahan jika ada"
                                variant="bordered"
                                {...register("description")}
                            />
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                color="danger"
                                variant="flat"
                                onPress={handleClose}
                                size="sm"
                                isDisabled={mutation.isLoading}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                type="submit"
                                isLoading={mutation.isLoading}
                                size="sm"
                            >
                                {isEdit ? "Update Barak" : "Tambah Barak"}
                            </Button>
                        </ModalFooter>
                    </form>
                )}
            </ModalContent>
        </Modal>
    );
};

export default BarakFormModal;
